import moment from "moment";
import {
	IAcknowledgementStatus,
	ICommunications,
	IEmployeeDetails,
	IHistoricalResult,
	IHistoricalUser,
	IPayPlanAssignment,
	ITimelineNode,
} from "../../types/historical";

export const getHistoricalPayPlan = (users: IHistoricalUser[], payPlanId: string) => {
	let historicalResul: IHistoricalResult[] = [];
	users.forEach((user: IHistoricalUser) => {
		//Upcoming Pay Plan Assignment
		if (user?.upcomingPayPlanAssignment?.planId !== null && user?.upcomingPayPlanAssignment?.planId?._id === payPlanId) {
			if (user.upcomingPayPlanAssignment.assignmentDate !== null) {
				const index = findHistoricalResult(user.upcomingPayPlanAssignment.assignmentDate, historicalResul);
				if (index !== -1) {
					historicalResul[index].users.push(user);
				} else {
					const historicalNew: IHistoricalResult = {
						assignmentDate: user.upcomingPayPlanAssignment.assignmentDate!,
						active: false,
						users: [user],
						type: user.upcomingPayPlanAssignment.assignmentTypeGroup ? "group" : "single",
						position: user.upcomingPayPlanAssignment.position,
						assigner: user.upcomingPayPlanAssignment.assignedBy ?? "Default",
						planId: payPlanId,
					};
					historicalResul.push(historicalNew);
				}
			}
		}
		//Pay Plan Assignment History
		if (user.payPlanAssignmentHistory?.length !== 0) {
			user.payPlanAssignmentHistory?.forEach((history: IPayPlanAssignment) => {
				if (history.planId !== null && history.planId?._id === payPlanId) {
					if (history.assignmentDate !== null) {
						const index = findHistoricalResult(history.assignmentDate, historicalResul);
						if (index !== -1) {
							historicalResul[index].users.push(user);
							if (!historicalResul[index].active) {
								historicalResul[index].active = true;
							}
						} else {
							const historicalNew: IHistoricalResult = {
								assignmentDate: history.assignmentDate!,
								active: true,
								users: [user],
								type: history.assignmentTypeGroup ? "group" : "single",
								position: history.position,
								assigner: history.assignedBy ?? "Default",
								planId: payPlanId,
							};
							historicalResul.push(historicalNew);
						}
					}
				}
			});
		}
	});
	historicalResul.sort((a, b) => {
		const dateA = moment(a.assignmentDate);
		const dateB = moment(b.assignmentDate);
		// Compare dates for sorting in descending order
		return dateB.diff(dateA);
	});

	return historicalResul;
};

const findHistoricalResult = (dateTime: string | undefined, historicalResul: IHistoricalResult[]) => {
	let positionIndex = -1;
	if (!dateTime) {
		return positionIndex;
	}
	historicalResul.forEach((item: IHistoricalResult, index: number) => {
		let dateTime1 = moment(dateTime);
		dateTime1 = dateTime1.startOf("day");
		let dateTime2 = moment(item.assignmentDate);
		dateTime2 = dateTime2.startOf("day");
		if (dateTime1.isSame(dateTime2)) {
			positionIndex = index;
		}
	});

	return positionIndex;
};

const getAssignmentDetails = (result: IHistoricalResult) => {
	const assignmentType = result.type == "group" ? "Group" : "Individual";
	const assigner = result.assigner;
	const position = result.position;
	const numberOfEmployees = result.users.length.toString();
	let assignmentDetails = "";
	if (assigner == "") {
		assignmentDetails =
			`${assignmentType} assignment done by Default Assignment to ${numberOfEmployees} \n` +
			`${result.users.length > 1 ? position + "s" : position}`;
	} else {
		assignmentDetails =
			`${assignmentType} assignment done by ${assigner} to ${numberOfEmployees} \n` +
			`${result.users.length > 1 ? position + "s" : position}`;
	}

	return assignmentDetails;
};

export const getTimelineNodes = (historicalResults: IHistoricalResult[], payPlanId: string) => {
	let nodes: ITimelineNode[] = [];
	historicalResults.forEach((result: IHistoricalResult) => {
		const assignmentDetails = getAssignmentDetails(result);
		const assigneeDetails: IEmployeeDetails[] = getAssigneeDetails(result.users, result.active, payPlanId);
		nodes.push({
			active: result.active,
			assignmentDetails: assignmentDetails,
			assignmentDate: result.assignmentDate,
			assigneeDetails: assigneeDetails,
			planId: result.planId,
		});
	});
	return nodes;
};

const getAssigneeDetails = (users: IHistoricalUser[], active: boolean, payPlanId: string) => {
	let assignees: IEmployeeDetails[] = [];
	users.forEach((user: IHistoricalUser) => {
		const newAssignee: IEmployeeDetails = getEmployeeDetails(user, active, payPlanId);
		assignees.push(newAssignee);
	});
	return assignees;
};

const getEmployeeDetails = (user: IHistoricalUser, active: boolean, payPlanId: string) => {
	return {
		_id: user._id,
		fullName: user.fullName,
		employeeId: user.employeeId,
		payrollPosition: user.position,
		email: getEmailFromPayPlan(user.communications),
		locationId: user.locationId,
		phoneNumber: getPhoneNumberFromPayPlan(user.communications),
		effectiveDate: active ? getEffectiveDateHistorical(user, payPlanId) : getEffectiveDateUpcoming(user),
		completedDate: active ? getDateFromHistorical(user, payPlanId, "COMPLETED") : getDateFromUpcoming(user, "COMPLETED"),
		inProgressDate: active ? getDateFromHistorical(user, payPlanId, "INPROGRESS") : getDateFromUpcoming(user, "INPROGRESS"),
		draftDate: active ? getDateFromHistorical(user, payPlanId, "DRAFT") : getDateFromUpcoming(user, "DRAFT"),
		declinedDate: active ? getDateFromHistorical(user, payPlanId, "DECLINED") : getDateFromUpcoming(user, "DECLINED"),
	};
};

const getEmailFromPayPlan = (communications: ICommunications[]) => {
	let email = "Unknown";
	communications.forEach((item: ICommunications) => {
		if (item.communicationType === "EMAIL" && item.usage === "BUSINESS") {
			email = item.value;
		}
	});

	return email;
};

const getPhoneNumberFromPayPlan = (communications: ICommunications[]) => {
	let phone = "Unknown";
	communications.forEach((item: ICommunications) => {
		if (item.communicationType === "PHONE" && item.usage === "BUSINESS") {
			phone = item.value;
		}
	});

	return phone;
};

const getEffectiveDateHistorical = (user: IHistoricalUser, payPlanId: string) => {
	let effectiveDate = "";
	user?.payPlanAssignmentHistory?.forEach((payPlanAssignment: IPayPlanAssignment) => {
		if (payPlanAssignment.planId !== null && payPlanAssignment.planId!._id === payPlanId) {
			effectiveDate = payPlanAssignment.effectiveDate;
		}
	});

	return effectiveDate;
};

const getEffectiveDateUpcoming = (user: IHistoricalUser) => {
	return user?.upcomingPayPlanAssignment?.effectiveDate;
};

const getDateFromUpcoming = (user: IHistoricalUser, statusText: string) => {
	let statusDate = "";
	user?.upcomingPayPlanAssignment?.acknowledgementStatus?.forEach((status: IAcknowledgementStatus) => {
		if (status.status.toUpperCase() === statusText) {
			statusDate = status.statusDate ?? "";
		}
	});

	return statusDate;
};

const getDateFromHistorical = (user: IHistoricalUser, payPlanId: string, statusText: string) => {
	let statusDate = "";
	user?.payPlanAssignmentHistory?.forEach((payPlanAssignment: IPayPlanAssignment) => {
		if (payPlanAssignment.planId !== null && payPlanAssignment.planId!._id === payPlanId) {
			payPlanAssignment.acknowledgementStatus?.forEach((status: IAcknowledgementStatus) => {
				if (status.status.toUpperCase() === statusText) {
					statusDate = status.statusDate ?? "";
				}
			});
		}
	});

	return statusDate;
};

export const getRanking = (ranking: number) => {
	// special cases for numbers with last two digits between 11-20
	if (ranking % 100 >= 11 && ranking % 100 <= 20) {
		return `${ranking.toString()}th`;
	}

	const lastDigit = ranking % 10;

	switch (lastDigit) {
		case 1:
			return `${ranking.toString()}st`;
		case 2:
			return `${ranking.toString()}nd`;
		case 3:
			return `${ranking.toString()}rd`;
		default:
			return `${ranking.toString()}th`;
	}
};
